<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  file: File | null
}>()

const emit = defineEmits<{
  (e: 'update:file', value: File | null): void
  (e: 'next'): void
}>()

const { t } = useI18n()

const dragActive = ref(false)
const fileError = ref<string | null>(null)

const ALLOWED_EXTENSIONS = ['.csv', '.xlsx']

function validateFile(file: File): boolean {
  fileError.value = null
  const name = file.name.toLowerCase()
  const valid = ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext))
  if (!valid) {
    fileError.value = t('views.trackRecord.upload.invalidExtension')
    return false
  }
  if (file.size === 0) {
    fileError.value = t('views.trackRecord.upload.emptyFile')
    return false
  }
  return true
}

function onFileInput(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  if (validateFile(file)) {
    emit('update:file', file)
  } else {
    emit('update:file', null)
  }
  // reset input so re-selecting same file triggers change event
  input.value = ''
}

function onDrop(event: DragEvent) {
  dragActive.value = false
  const file = event.dataTransfer?.files?.[0]
  if (!file) return
  if (validateFile(file)) {
    emit('update:file', file)
  } else {
    emit('update:file', null)
  }
}

function onDragOver() {
  dragActive.value = true
}

function onDragLeave() {
  dragActive.value = false
}

function clearFile() {
  emit('update:file', null)
  fileError.value = null
}

const canContinue = () => props.file !== null && fileError.value === null
</script>

<template>
  <v-row>
    <!-- Upload zone -->
    <v-col cols="12">
      <div
        class="drop-zone"
        :class="{ 'drop-zone--active': dragActive }"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <template v-if="!file">
          <v-icon size="40" icon="PhUploadSimple" class="mb-2 text-medium-emphasis" />
          <p class="text-body-2 text-medium-emphasis text-center mb-3">
            {{ dragActive ? t('views.trackRecord.upload.dragActive') : t('views.trackRecord.upload.hint') }}
          </p>
          <label>
            <v-btn variant="outlined" size="small" tag="span">
              {{ t('views.trackRecord.upload.label') }}
            </v-btn>
            <input
              type="file"
              accept=".csv,.xlsx"
              class="d-none"
              @change="onFileInput"
            />
          </label>
        </template>

        <template v-else>
          <v-icon size="36" icon="PhFileText" class="mb-2 text-primary" />
          <p class="text-body-2 font-weight-medium mb-1">{{ file.name }}</p>
          <p class="text-caption text-medium-emphasis mb-3">
            {{ (file.size / 1024).toFixed(1) }} Ko
          </p>
          <v-btn variant="text" size="small" color="error" @click="clearFile">
            {{ t('views.trackRecord.upload.changeFile') }}
          </v-btn>
        </template>
      </div>

      <v-alert
        v-if="fileError"
        type="error"
        density="compact"
        class="mt-2"
        :text="fileError"
      />
    </v-col>

    <!-- Navigation -->
    <v-col cols="12" class="d-flex justify-end">
      <v-btn
        :disabled="!canContinue()"
        @click="emit('next')"
      >
        {{ t('views.trackRecord.buttons.next') }}
        <template #append>
          <v-icon icon="PhArrowRight" />
        </template>
      </v-btn>
    </v-col>
  </v-row>
</template>

<style scoped>
.drop-zone {
  border: 2px dashed rgba(var(--v-border-color), 0.4);
  border-radius: 8px;
  padding: 32px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
}

.drop-zone--active {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.04);
}
</style>
